import { NgClass } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import * as fromGenerated from '../../_generated';
import * as fromLayout from '../../layout';
import { AssistantLogoComponent } from '../assistants/assistant-logo.component';
import * as fromAssistantIcon from '../assistants/assistants.icon.helper';
import { RECENT_CHATS_LIMIT } from '../chats.constants';
import * as fromShared from '../shared';
import { ChatsActions, chatsFeature, ChatsState } from '../store';
import { ChatActions } from '../store/chats.actions';
import { NavigationChatsListSmartComponent } from './navigation/navigation-chats-list.smart.component';
import { NavigationSourcesListSmartComponent } from './navigation/navigation-sources-list.smart.component';
import { TopBarMobileResponsive } from './top-bar.mobile.responsive';

const ANIMATION_CHAT_RIGHT_PANEL_DURATION = 300;

export type AssistantProfileTabOptionKey = 'sources' | 'recent-chats';

@Component({
  selector: 'squadbox-chat-assistant-profile',
  imports: [
    fromSeriousSystem.UseUIBasicIconDirective,
    fromSeriousSystem.UseAssistantCustomIconDirective,
    fromSeriousSystem.UseAssistantIconDirective,
    fromSeriousSystem.IconButtonDirective,
    fromSeriousSystem.UseArrowIconDirective,
    fromSeriousSystem.UseNavigationIconDirective,
    fromSeriousSystem.TabsComponent,
    TranslateModule,
    NgClass,
    TopBarMobileResponsive,
    NavigationChatsListSmartComponent,
    NavigationSourcesListSmartComponent,
    AssistantLogoComponent,
  ],
  template: `
    <!-- Section 1: Top Bar -->
    @if (isMobile()) {
      <squadbox-top-bar-mobile
        class="[&>div]:border-b-0"
        (chevronLeftButtonClicked)="handleClickToggleAssistantProfileCTA()"
        [hideMoreOptionsButton]="true"
      ></squadbox-top-bar-mobile>
    } @else {
      <div class="flex items-center justify-between h-20">
        <button
          sdIconButton
          variant="icon"
          color="neutral"
          size="md"
          (click)="handleClickToggleAssistantProfileCTA()"
        >
          <svg sdUseArrowIcon="double-chevron-right"></svg>
        </button>
      </div>
    }
    <!-- Section 2: Logo, Name, Description -->
    <div
      class="max-h-1/2 flex flex-col mb-2 gap-2"
      [ngClass]="{
        'px-8': isMobile(),
        'px-3': !isMobile(),
      }"
    >
      <div
        class="
          flex items-center justify-center w-min mb-2
          bg-primary-50 rounded-lg
        "
        [ngClass]="{
          'p-4': isMobile(),
          'p-5': !isMobile(),
        }"
      >
        <squadbox-assistant-logo
          class="
            [&>svg]:text-primary-300 [&>svg]:size-6 [&>img]:size-6
            [&>svg]:shrink-0 [&>img]:shrink-0
          "
          [logo]="selectedAssistant().logo"
        />
      </div>
      <h1
        class="font-semibold wrap-anywhere"
        [ngClass]="{
          'typo-p2': isMobile(),
          'typo-p3': !isMobile(),
        }"
      >
        {{ selectedAssistant().name }}
      </h1>
      <p
        class="
          flex flex-col
          gap-1
          overflow-y-hidden
          p-2 text-neutral-700
          bg-neutral-50 rounded
        "
        [ngClass]="{
          'typo-p3': isMobile(),
          'typo-caption': !isMobile(),
        }"
      >
        <span
          #descriptionElement
          class="line-clamp-2 wrap-anywhere"
          [class.line-clamp-none]="isDescriptionExpanded()"
          [class.overflow-y-auto]="isDescriptionExpanded()"
        >
          {{ selectedAssistant().description }}
        </span>
        @if (isDescriptionClamped()) {
          @if (!isDescriptionExpanded()) {
            <a
              role="button"
              tabindex="0"
              class="text-primary-500 font-semibold cursor-pointer"
              (click)="handleClickToggleExpandDescriptionCTA()"
              (keydown.enter)="handleClickToggleExpandDescriptionCTA()"
              (keydown.space)="
                handleClickToggleExpandDescriptionCTA(); $event.preventDefault()
              "
              >{{ 'CHAT.ASSISTANT_PROFILE.SEE_MORE' | translate }}</a
            >
          } @else {
            <a
              role="button"
              tabindex="0"
              class="text-primary-500 font-semibold cursor-pointer"
              (click)="handleClickToggleExpandDescriptionCTA()"
              (keydown.enter)="handleClickToggleExpandDescriptionCTA()"
              (keydown.space)="
                handleClickToggleExpandDescriptionCTA(); $event.preventDefault()
              "
              >{{ 'CHAT.ASSISTANT_PROFILE.SEE_LESS' | translate }}</a
            >
          }
        }
      </p>
    </div>
    <!-- Section 3: Tabs -->
    <div class="px-8 tablet-portrait:px-3 mb-6">
      <sd-tabs
        class="[&_button]:flex-1"
        [tabOptions]="tabOptions()"
        [isMobile]="isMobile()"
        [selectedTabKey]="selectedTabKey()"
        (tabClicked)="handleClickTabCTA($event)"
      />
    </div>
    <!-- Tab Content -->
    <div class="flex flex-1 flex-col gap-1 overflow-y-auto pb-6">
      @switch (selectedTabKey()) {
        @case ('sources') {
          <!-- Tab Content: Sources -->
          <squadbox-navigation-sources-list-smart
            [selectedAssistant]="selectedAssistant()"
          >
          </squadbox-navigation-sources-list-smart>
        }
        @case ('recent-chats') {
          <!-- Tab Content: Recent Chats -->
          <squadbox-navigation-chats-list-smart
            [label]="'CHAT.ASSISTANT_PROFILE.RECENT_CHATS' | translate"
            [chatNavigationItems]="chatNavigationItems()"
            (chatClicked)="handleChatClicked()"
            (chatDeleteClicked)="chatDeleteClicked.emit($event)"
          >
          </squadbox-navigation-chats-list-smart>
        }
      }
    </div>
  `,
  styles: [
    `
      @reference '../../../styles.css';

      :host {
        @apply flex flex-col;
        @apply h-full;
        @apply tablet-portrait:px-2;
        @apply bg-shades-white;
      }
    `,
  ],
})
export class ChatAssistantProfileComponent implements AfterViewInit {
  public readonly selectedAssistant =
    input.required<
      Pick<
        fromGenerated.AssistantView,
        'uuid' | 'name' | 'logo' | 'description' | 'documents' | 'trait'
      >
    >();
  public readonly chatDeleteClicked = output<number>();

  @ViewChild('descriptionElement')
  descriptionElement!: ElementRef<HTMLParagraphElement>;

  protected readonly fromAssistantIcon = fromAssistantIcon;
  private readonly chatsStore = inject<Store<ChatsState>>(Store);
  private readonly layoutStore = inject<Store<fromLayout.LayoutState>>(Store);
  private readonly translateService = inject(TranslateService);

  public readonly isMobile = this.layoutStore.selectSignal(
    fromLayout.layoutFeature.selectIsMobile,
  );
  public readonly viewMode = this.layoutStore.selectSignal(
    fromLayout.layoutFeature.selectViewMode,
  );
  private readonly assistantProfileOpened = this.chatsStore.selectSignal(
    chatsFeature.selectIsAssistantProfileOpened,
  );
  public readonly chatNavigationItems = computed(() =>
    this.chatsStore
      .selectSignal(fromShared.selectChatNavigationItemsBySelectedAssistant)()
      .slice(0, RECENT_CHATS_LIMIT),
  );

  public readonly selectedTabKey =
    signal<AssistantProfileTabOptionKey>('sources');
  public readonly tabOptions = computed(
    () =>
      [
        {
          key: 'sources' as AssistantProfileTabOptionKey,
          label: this.translateService.instant(
            'CHAT.ASSISTANT_PROFILE.TABS.DETAILS',
          ) as string,
        },
        {
          key: 'recent-chats' as AssistantProfileTabOptionKey,
          label: this.translateService.instant(
            'CHAT.ASSISTANT_PROFILE.TABS.CONVERSATIONS',
          ) as string,
        },
      ] satisfies fromSeriousSystem.TabOption<AssistantProfileTabOptionKey>[],
  );

  public readonly isDescriptionClamped = signal(false);
  public readonly isDescriptionExpanded = signal(false);

  constructor() {
    effect(() => {
      this.isDescriptionExpanded.set(false);
      this.selectedAssistant();
      this.checkIfDescriptionIsClamped();
    });

    effect(() => {
      this.assistantProfileOpened();
      untracked(() => {
        setTimeout(
          () => {
            this.checkIfDescriptionIsClamped();
          },
          this.isMobile() ? 0 : ANIMATION_CHAT_RIGHT_PANEL_DURATION,
        );
      });
    });

    /**
     * Load the chats for the selected assistant when the selected tab is 'recent-chats'
     */
    effect(() => {
      const selectedAssistant = this.selectedAssistant();
      const selectedTabKey = this.selectedTabKey();

      if (selectedAssistant && selectedTabKey === 'recent-chats') {
        this.chatsStore.dispatch(
          ChatsActions.loadChatsForAssistant({
            assistantUuid: selectedAssistant.uuid,
          }),
        );
      }
    });
  }

  ngAfterViewInit() {
    setTimeout(
      () => {
        this.checkIfDescriptionIsClamped();
      },
      this.isMobile() ? 0 : ANIMATION_CHAT_RIGHT_PANEL_DURATION,
    );
  }

  public handleClickToggleAssistantProfileCTA() {
    if (this.viewMode() === 'conversation') {
      this.chatsStore.dispatch(ChatActions.toggleAssistantProfile());
    } else {
      this.chatsStore.dispatch(
        fromShared.DocumentViewerActions.toggleAssistantProfileAndConversationViewMode(),
      );
    }
  }

  public handleChatClicked() {
    if (this.isMobile()) {
      this.chatsStore.dispatch(ChatActions.closeAssistantProfile());
    }
  }

  private checkIfDescriptionIsClamped() {
    const element = this.descriptionElement?.nativeElement;
    if (!element) return;

    setTimeout(() => {
      // Check if the element is clamped by comparing scroll height to client height
      this.isDescriptionClamped.set(
        element.scrollHeight > element.clientHeight,
      );
    }, 0);
  }

  public handleClickToggleExpandDescriptionCTA() {
    this.isDescriptionExpanded.set(!this.isDescriptionExpanded());
  }

  public handleClickTabCTA(
    tab: fromSeriousSystem.TabOption<AssistantProfileTabOptionKey>,
  ) {
    this.selectedTabKey.set(tab.key);
  }
}
